import { useState } from 'react';
import { useAuth } from '../../contexts';
import styles from './SuspendedScreen.module.css';

/**
 * Empresas liberadas para onde a pessoa pode ir quando a ativa esta
 * bloqueada. Quem participa de outras empresas nunca fica preso numa so.
 */
export function TrocarDeEmpresa() {
    const { empresas, empresaAtiva, selecionarEmpresa } = useAuth();
    const outras = empresas.filter((e) => e.id !== empresaAtiva?.id && e.estado === 'liberada');
    if (outras.length === 0) return null;

    return (
        <div className={styles.outras}>
            <p className={styles.outrasTitulo}>Continuar em outra empresa</p>
            {outras.map((e) => (
                <button key={e.id} className={styles.outraBtn} onClick={() => selecionarEmpresa(e.id)}>
                    {e.name}
                </button>
            ))}
        </div>
    );
}

/**
 * Cria a empresa propria (plano atual ou o gratuito). Serve a quem so participa
 * de empresas de outros: sem ela, quem fica sem empresa liberada so podia sair.
 */
function CriarMinhaEmpresa({ destaque }: { destaque: boolean }) {
    const { criarMinhaEmpresa } = useAuth();
    const [criando, setCriando] = useState(false);
    const [erro, setErro] = useState('');

    const criar = async () => {
        setCriando(true);
        setErro('');
        try {
            await criarMinhaEmpresa();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível criar a empresa');
        } finally {
            setCriando(false);
        }
    };

    const botao = (
        <button className={destaque ? styles.payBtn : styles.outraBtn} onClick={criar} disabled={criando}>
            {criando ? 'Criando...' : 'Criar minha empresa'}
        </button>
    );

    return (
        <>
            {erro && <p className={styles.erro}>{erro}</p>}
            {/* Ao lado de outras empresas liberadas, entra como mais uma opcao. */}
            {destaque ? botao : <div className={styles.outras}>{botao}</div>}
        </>
    );
}

/**
 * Empresa de outra pessoa bloqueada pela cobranca do dono. O convidado nunca ve
 * tela de pagamento: quem resolve e o dono.
 */
export function EmpresaBloqueadaScreen({ estado }: { estado: 'empresa-suspensa' | 'empresa-inativa' }) {
    const { empresas, empresaAtiva, logout } = useAuth();
    const nome = empresaAtiva?.name ?? 'Esta empresa';
    const temPropria = empresas.some((e) => e.souDono);
    const temOutraLiberada = empresas.some((e) => e.id !== empresaAtiva?.id && e.estado === 'liberada');

    return (
        <div className={styles.overlay}>
            <div className={styles.card}>
                <div className={styles.icon}>🔒</div>
                <h1 className={styles.title}>
                    {estado === 'empresa-suspensa' ? 'Acesso suspenso' : 'Empresa inativa'}
                </h1>
                <p className={styles.body}>
                    {estado === 'empresa-suspensa'
                        ? `${nome} está com o acesso suspenso por uma pendência na assinatura. Fale com o responsável pela empresa para regularizar.`
                        : `${nome} não está mais ativa no Financeiro Diário. Fale com o responsável pela empresa.`}
                </p>
                <p className={styles.hint}>Seus dados e os da empresa continuam guardados.</p>
                <TrocarDeEmpresa />
                {/* Sem empresa propria, a saida e criar a dela (o cadastro recusa e-mail que ja tem conta). */}
                {!temPropria && <CriarMinhaEmpresa destaque={!temOutraLiberada} />}
                <button className={styles.logoutBtn} onClick={logout}>
                    Sair da conta
                </button>
            </div>
        </div>
    );
}

/**
 * Nao participa de nenhuma empresa (saiu de todas em que foi convidado, ou
 * nunca teve a propria). So resta criar a dele (plano padrao, se nao tiver um).
 */
export function SemEmpresaScreen() {
    const { user, logout } = useAuth();

    return (
        <div className={styles.overlay}>
            <div className={styles.card}>
                <div className={styles.icon}>🏢</div>
                <h1 className={styles.title}>Você não participa de nenhuma empresa</h1>
                <p className={styles.body}>
                    O acesso às empresas em que você foi convidado terminou. Você pode criar a sua própria empresa,
                    {user?.planId ? ' no seu plano atual' : ' no plano gratuito'}, ou aguardar um novo convite.
                </p>
                <p className={styles.hint}>Se foi engano, fale com quem convidou você.</p>
                <CriarMinhaEmpresa destaque />
                <button className={styles.logoutBtn} onClick={logout}>
                    Sair da conta
                </button>
            </div>
        </div>
    );
}
